import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Building2, GraduationCap, Plus, RotateCw, School, Search } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Pagination } from '@/components/ui/Pagination';
import { QueryError } from '@/components/ui/QueryError';
import { Select } from '@/components/ui/Select';
import { Tabs, tabPanelId, type TabOption } from '@/components/ui/Tabs';
import {
  INDIAN_STATES,
  SCHOOL_STATUS_LABEL,
  STATE_OPTIONS,
  type InstitutionType,
  type InstituteStatus,
} from '@/constants/institute';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { toast } from '@/hooks/useToast';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { ApiRequestError } from '@/lib/apiClient';
import type { InstituteListItem } from '@/types/institute';
import { DeckPageHeader } from '../../components/DeckPageHeader';
import { useIsSuperAdmin } from '../../hooks/useIsSuperAdmin';
import { institutesApi } from '../api/institutes.api';
import { InstituteCard, InstituteCardSkeleton } from '../components/InstituteCard';
import { StatusFilter } from '../components/StatusFilter';

/**
 * Cards on a page.
 *
 * Ten fills the grid without asking anyone to scroll past what they came for;
 * the eleventh customer is what the page controls below are for.
 */
const PAGE_SIZE = 10;

/**
 * The grid the cards sit in.
 *
 * Two abreast is the widest a card stays readable at this container width;
 * three only from `xl`, where there is room for the feature pills not to wrap.
 */
const CARD_GRID = 'grid gap-4 sm:grid-cols-2 xl:grid-cols-3';

/**
 * The two views the tab strip offers.
 *
 * There is no "all" view: a college and a school run different halves of the
 * product, so a mixed list is a list nobody is actually reading. The tab is the
 * kind, which is why the cards no longer need to repeat it.
 */
type InstituteKind = InstitutionType;

/** The view the console opens on. */
const DEFAULT_KIND: InstituteKind = 'SCHOOL';

/**
 * The screen's filters, as they sit in the address bar.
 *
 * `institutionType` is always a real kind — the list request always carries
 * one, because every view of this page is either schools or colleges.
 */
type Filters = {
  page: number;
  search: string;
  status: InstituteStatus | '';
  state: string;
  institutionType: InstituteKind;
};

const DEFAULT_FILTERS: Filters = {
  page: 1,
  search: '',
  status: '',
  state: '',
  institutionType: DEFAULT_KIND,
};

/**
 * What a link may set. This is also what makes the overview's tiles worth
 * clicking: "Suspended: 3" links here with `?status=SUSPENDED`, and lands on
 * those three. A mistyped value shows everything rather than an error.
 */
const FILTER_OPTIONS = {
  allowed: {
    status: Object.keys(SCHOOL_STATUS_LABEL),
    state: INDIAN_STATES,
    institutionType: ['SCHOOL', 'COLLEGE'],
  },
};

/**
 * Every institute on the platform, as a grid of cards.
 *
 * Each card answers the questions we actually ask about a customer: who they
 * are, where they are, whether the people we opened accounts for are using
 * them, how much of what they asked for is live, and whether they are.
 */
export function InstitutesListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // Adding, editing and removing a customer are SUPER_ADMIN writes; support
  // browses the same grid without being offered them.
  const canManage = useIsSuperAdmin();
  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS, FILTER_OPTIONS);
  // Seeded from the address, so a restored search is shown in the box too.
  const [searchDraft, setSearchDraft] = useState(filters.search);
  const settledSearch = useDebouncedValue(searchDraft.trim(), 300);

  // Searches as the operator types, once they pause — one request per pause,
  // not per keystroke. Enter still searches at once.
  useEffect(() => {
    if (settledSearch !== filters.search) updateFilter({ search: settledSearch });
    // Only a settled draft should search; the address catching up is not one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settledSearch]);

  const { data, isLoading, error, refetch } = useApiQuery(
    () => institutesApi.list({ ...filters, limit: PAGE_SIZE }),
    [filters.page, filters.search, filters.status, filters.state, filters.institutionType],
  );

  // Removing a customer cannot be undone from the console, so the dialog asks
  // for the institute's code typed out — a click alone is too easy to misplace
  // on a grid of near-identical cards.
  const [removing, setRemoving] = useState<InstituteListItem | null>(null);
  const [removeConfirmation, setRemoveConfirmation] = useState('');
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  function closeRemoveDialog() {
    setRemoving(null);
    setRemoveConfirmation('');
    setRemoveError(null);
  }

  async function confirmRemove() {
    if (!removing) return;
    setIsRemoving(true);
    setRemoveError(null);
    try {
      await institutesApi.remove(removing.uuid);
      toast.success(`${removing.displayName} has been removed`);
      closeRemoveDialog();
      refetch();
    } catch (caught) {
      setRemoveError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsRemoving(false);
    }
  }

  /**
   * The two views, with what each holds.
   *
   * Counted over the same search, status and state as the grid, so switching
   * tab never shows a number that disagreed with the cards underneath it.
   */
  const byType = data?.byType;
  const tabs: TabOption<InstituteKind>[] = [
    {
      value: 'SCHOOL',
      label: 'Schools',
      icon: <School className="h-4 w-4" aria-hidden />,
      badge: byType?.SCHOOL,
    },
    {
      value: 'COLLEGE',
      label: 'Colleges',
      icon: <GraduationCap className="h-4 w-4" aria-hidden />,
      badge: byType?.COLLEGE,
    },
  ];

  /** The kind the reader is not looking at — what the empty view points to. */
  const otherKind: InstituteKind = filters.institutionType === 'COLLEGE' ? 'SCHOOL' : 'COLLEGE';
  const otherKindCount = byType?.[otherKind] ?? 0;

  /** Filters other than the page — used to decide if "clear" should show. */
  const hasActiveFilters = Boolean(filters.search || filters.status || filters.state);

  function updateFilter(patch: Partial<Filters>) {
    // Any filter change returns to page 1 — staying on page 7 of a new result
    // set shows an empty table for no obvious reason.
    setFilters({ ...patch, page: patch.page ?? 1 });
  }

  const institutes = data?.items ?? [];

  /** What to say when the grid has nothing in it — three different reasons. */
  const emptyState =
    /*
      A tab with nothing behind it is not an empty platform. Without this
      branch, opening "Colleges" at a school-only customer base read "No
      institutes yet — add the first institute", which is wrong twice over:
      there are institutes, and the fix is not necessarily to add one.
    */
    !hasActiveFilters && otherKindCount > 0 ? (
      <EmptyState
        icon={<Building2 className="h-5 w-5" />}
        title={filters.institutionType === 'COLLEGE' ? 'No colleges yet' : 'No schools yet'}
        description={
          filters.institutionType === 'COLLEGE'
            ? 'Every customer on the platform is a school. A new institute is set to one kind or the other when it is created.'
            : 'Every customer on the platform is a college. A new institute is set to one kind or the other when it is created.'
        }
        action={
          <Button variant="secondary" onClick={() => updateFilter({ institutionType: otherKind })}>
            {otherKind === 'COLLEGE' ? 'View colleges' : 'View schools'}
          </Button>
        }
      />
    ) : hasActiveFilters ? (
      <EmptyState
        icon={<Search className="h-5 w-5" />}
        title="No institutes match those filters"
        description="Try a different search, or clear the filters to see every institute."
        action={
          <Button
            variant="secondary"
            leftIcon={<RotateCw className="h-4 w-4" />}
            onClick={() => {
              setSearchDraft('');
              updateFilter({ search: '', status: '', state: '' });
            }}
          >
            Clear filters
          </Button>
        }
      />
    ) : (
      <EmptyState
        icon={<Building2 className="h-5 w-5" />}
        title="No institutes yet"
        description={
          canManage
            ? 'Add the first institute to hand the product over to its administrator.'
            : 'Institutes appear here once a super administrator adds them.'
        }
        action={
          canManage && (
            <Button
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => navigate('/platform/institutes/new')}
            >
              Add Institute
            </Button>
          )
        }
      />
    );

  return (
    <>
      <DeckPageHeader
        eyebrow="Customers"
        title="Institutes"
        description="Every institute using the product."
        actions={
          canManage && (
            <Button
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => navigate('/platform/institutes/new')}
              className="h-10 font-bold text-base px-3"
            >
              Add Institute
            </Button>
          )
        }
      />

      {/*
        Schools and colleges, in one page rather than two.

        A segmented switch rather than a dropdown: this is the one answer half
        the product branches on, so it is the split a reader makes first and
        without opening anything. The search, status and state below stay
        applied across both — switching narrows what you are already looking at
        rather than starting again.
      */}
      <Tabs
        options={tabs}
        value={filters.institutionType}
        onChange={(institutionType) => updateFilter({ institutionType })}
        label="Kind of institute"
        variant="segmented"
        className="w-full sm:w-auto sm:self-start"
      />

      <div
        id={tabPanelId(filters.institutionType)}
        role="tabpanel"
        aria-labelledby={`${filters.institutionType}-tab`}
        className="flex flex-col gap-4"
      >
        {/* ---------------- filters ----------------
            All of them in the open. They used to sit behind a "Filters" toggle,
            which hid the two questions this screen is opened to answer — "which
            ones are suspended?" and "which ones in Kerala?" — behind a click
            that gave no hint of what was inside. */}
        <div className="flex flex-col gap-3.5 rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 sm:p-5 shadow-[0_2px_8px_rgba(0,0,0,0.03)]">
          <div className="flex flex-wrap items-end gap-3">
            <form
              className="flex min-w-[280px] flex-1 flex-col gap-1.5"
              onSubmit={(event) => {
                event.preventDefault();
                updateFilter({ search: searchDraft.trim() });
              }}
            >
              <label htmlFor="institutes-search-input" className="lbl text-xs font-bold text-slate-700 dark:text-slate-300">
                Search
              </label>
              <Input
                id="institutes-search-input"
                label="Search"
                hideLabel
                placeholder="Name, code, city or UDISE"
                value={searchDraft}
                onChange={(event) => setSearchDraft(event.target.value)}
                leftIcon={<Search className="h-4 w-4" />}
              />
            </form>

            <div className="w-full sm:w-56 shrink-0">
              <Select
                label="State"
                placeholder="Any state"
                value={filters.state}
                options={STATE_OPTIONS}
                onChange={(event) => updateFilter({ state: event.target.value })}
              />
            </div>
          </div>

          <StatusFilter
            value={filters.status ?? ''}
            onChange={(status) => updateFilter({ status })}
            counts={data?.byStatus}
          />
        </div>

        {/* ---------------- results ---------------- */}
        {error ? (
          <QueryError title="Could not load institutes" error={error} onRetry={refetch} />
        ) : isLoading ? (
          <div className={CARD_GRID}>
            {/* As many placeholders as the page holds, so the grid does not
                grow under the reader when the real cards land. */}
            {Array.from({ length: PAGE_SIZE }, (_, index) => (
              <InstituteCardSkeleton key={index} />
            ))}
          </div>
        ) : institutes.length === 0 ? (
          <div className="card">{emptyState}</div>
        ) : (
          <>
            <div className={CARD_GRID}>
              {institutes.map((institute) => (
                <InstituteCard
                  key={institute.uuid}
                  institute={institute}
                  // The edit screen reads this to know its "back" comes here,
                  // rather than dropping the reader on a record they never
                  // opened.
                  onEdit={
                    canManage
                      ? () =>
                          navigate(`/platform/institutes/${institute.uuid}/edit`, {
                            state: { from: location.pathname + location.search },
                          })
                      : undefined
                  }
                  // A live customer is suspended first; only then is removal offered.
                  onRemove={
                    canManage && institute.status === 'SUSPENDED' ? setRemoving : undefined
                  }
                />
              ))}
            </div>

            {/* Only once there is a second page. A "1 / 1" strip under ten
                cards is a control that can do nothing. */}
            {data && data.meta.totalPages > 1 && (
              <div className="card overflow-hidden">
                <Pagination meta={data.meta} onPageChange={(page) => updateFilter({ page })} />
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmDialog
        isOpen={removing !== null}
        title={`Remove ${removing?.displayName ?? 'this institute'}?`}
        description="It leaves every list, count and dashboard on the console, and nobody there can sign in again."
        confirmLabel="Remove institute"
        tone="danger"
        dangerNote={
          <>
            <span className="font-[560] text-crit">This cannot be undone from the console.</span>{' '}
            The records are kept for audit, but the institute will not appear here again.
          </>
        }
        isLoading={isRemoving}
        isConfirmDisabled={removeConfirmation.trim() !== removing?.code}
        error={removeError}
        onConfirm={confirmRemove}
        onCancel={closeRemoveDialog}
      >
        <Input
          label={
            <>
              Type <span className="font-mono font-semibold">{removing?.code}</span> to confirm
            </>
          }
          value={removeConfirmation}
          autoComplete="off"
          spellCheck={false}
          onChange={(event) => setRemoveConfirmation(event.target.value)}
        />
      </ConfirmDialog>
    </>
  );
}
